import { useState, FormEvent } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { invitationsApi } from '../api/organizations';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { PASSWORD_MIN } from '../utils/password';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

export default function SignupPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    organizationName: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    message: '',
  });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await invitationsApi.signupOrg(form);
      setSuccess(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLoading && isAuthenticated) return <Navigate to="/" replace />;

  const inputCls =
    'w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl text-sm text-zce-navy focus:bg-white focus:ring-2 focus:ring-zce-navy/30 focus:border-zce-navy transition-all duration-200 placeholder:text-gray-400';
  const labelCls = 'block text-xs font-semibold text-zce-navy mb-1.5 uppercase tracking-wide';

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-10 bg-gradient-to-br from-zce-navy via-zce-navy-light to-zce-navy zce-gradient-anim">
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl zce-float-orb pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] bg-purple-400/20 rounded-full blur-3xl zce-float-orb pointer-events-none" style={{ animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl zce-float-orb pointer-events-none" style={{ animationDelay: '4s' }} />

      <div className="relative w-full max-w-xl zce-fade-in">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-black/20 border border-white/40 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500" />
          <div className="p-8 sm:p-10">
            <div className="text-center mb-7">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-5 bg-gradient-to-br from-zce-navy to-zce-navy-light shadow-lg shadow-zce-navy/30 transform hover:scale-105 hover:rotate-3 transition-all duration-300">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-9a4 4 0 11-8 0 4 4 0 018 0zm6 4a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-zce-navy tracking-tight">{t('team_signup_title')}</h1>
              <p className="text-zce-gray-muted mt-2 text-sm max-w-md mx-auto">{t('team_signup_subtitle')}</p>
            </div>

            {success ? (
              <div className="space-y-5">
                <div className="flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-bold text-zce-navy mb-2">{t('team_signup_welcome')}</h3>
                  <p className="text-sm text-zce-gray-muted">{t('team_signup_created')}</p>
                </div>
                <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
                  <p className="font-semibold mb-1">{t('team_signup_canLogin')}</p>
                  <ul className="list-disc list-inside space-y-0.5 text-emerald-800">
                    <li>{t('team_signup_next1')}</li>
                    <li>{t('team_signup_next2')}</li>
                    <li>{t('team_signup_next3')}</li>
                  </ul>
                </div>
                <button onClick={() => navigate('/login')} className="w-full py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-zce-navy to-zce-navy-light hover:from-zce-navy-dark hover:to-zce-navy shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]">
                  {t('team_backToLogin')}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label htmlFor="orgName" className={labelCls}>{t('team_signup_orgName')} <span className="text-rose-500">*</span></label>
                  <input id="orgName" required value={form.organizationName} onChange={handleChange('organizationName')} className={inputCls} placeholder={t('team_signup_orgPh')} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="firstName" className={labelCls}>{t('team_firstName')} <span className="text-rose-500">*</span></label>
                    <input id="firstName" required value={form.firstName} onChange={handleChange('firstName')} className={inputCls} placeholder={t('team_signup_firstPh')} />
                  </div>
                  <div>
                    <label htmlFor="lastName" className={labelCls}>{t('team_lastName')} <span className="text-rose-500">*</span></label>
                    <input id="lastName" required value={form.lastName} onChange={handleChange('lastName')} className={inputCls} placeholder={t('team_signup_lastPh')} />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className={labelCls}>{t('team_email')} <span className="text-rose-500">*</span></label>
                  <input id="email" type="email" required value={form.email} onChange={handleChange('email')} className={inputCls} placeholder={t('team_signup_emailPh')} autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="password" className={labelCls}>{t('team_password')} <span className="text-rose-500">*</span></label>
                  <div className="relative">
                    <input id="password" type={showPwd ? 'text' : 'password'} required minLength={PASSWORD_MIN} value={form.password} onChange={handleChange('password')} className={inputCls + ' pr-12'} placeholder={t('team_signup_pwdPh', { n: PASSWORD_MIN })} autoComplete="new-password" />
                    <button type="button" tabIndex={-1} onClick={() => setShowPwd((s) => !s)} className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-zce-gray-muted hover:text-zce-navy transition-colors" aria-label={showPwd ? t('team_hidePwd') : t('team_showPwd')}>
                      {showPwd ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      )}
                    </button>
                  </div>
                  <PasswordStrengthMeter password={form.password} />
                </div>
                <div>
                  <label htmlFor="message" className={labelCls}>{t('team_signup_message')} <span className="text-zce-gray-muted normal-case font-normal">{t('team_optional')}</span></label>
                  <textarea id="message" value={form.message} onChange={handleChange('message')} rows={3} className={inputCls + ' resize-none'} placeholder={t('team_signup_messagePh')} />
                </div>

                {error && (
                  <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2.5">
                    <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-red-700 text-sm font-medium">{error}</span>
                  </div>
                )}

                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-start gap-2">
                  <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  <span>{t('team_signup_instant')}</span>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <button type="button" onClick={() => navigate('/login')} disabled={submitting} className="flex-1 py-3 px-4 rounded-xl text-sm font-semibold text-zce-navy bg-white border border-gray-300 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 transform hover:scale-[1.02] active:scale-[0.98]">
                    {t('team_cancel')}
                  </button>
                  <button type="submit" disabled={submitting} className="flex-1 py-3 px-4 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-zce-navy to-zce-navy-light hover:from-zce-navy-dark hover:to-zce-navy shadow-lg shadow-zce-navy/30 hover:shadow-xl hover:shadow-zce-navy/40 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center justify-center gap-2">
                    {submitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                        {t('team_sending')}
                      </>
                    ) : (
                      <>
                        {t('team_signup_send')}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                      </>
                    )}
                  </button>
                </div>

                <div className="text-center pt-3">
                  <p className="text-xs text-zce-gray-muted">
                    {t('team_signup_haveAccount')}{' '}
                    <Link to="/login" className="font-semibold text-zce-navy-light hover:text-zce-navy hover:underline transition-colors">{t('team_signIn')}</Link>
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-2 text-white/70 text-xs">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
          <span>{t('team_secure')}</span>
        </div>
      </div>
    </div>
  );
}
